import { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { usePortalLogout } from '@/hooks/usePortalLogout';
import { useUnreadNotifications } from '@/hooks/useUnreadNotifications';
import SidebarProjectsNavSection from '@/components/feature/SidebarProjectsNavSection';
import { getProfileDisplayName } from '@/lib/profileDisplayName';
import { applySubcontractorLanguageDefault } from '@/lib/languagePreference';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';
import LogoutConfirmModal from '@/components/feature/LogoutConfirmModal';
import ChangePasswordModal from '@/components/feature/ChangePasswordModal';
import SidebarAccountMenu from '@/components/feature/SidebarAccountMenu';

interface SubLayoutProps {
  children: React.ReactNode;
  title?: string;
  showBack?: boolean;
  backTo?: string;
  actions?: React.ReactNode;
}

export default function SubLayout({ children, title, showBack, backTo, actions }: SubLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { profile, user, loading: authLoading } = useAuth();
  const accountName = authLoading
    ? '...'
    : getProfileDisplayName(profile, t('user.subcontractor'), user?.email);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const {
    logoutConfirm,
    openLogoutConfirm,
    closeLogoutConfirm,
    performLogout,
    isSigningOut,
    logoutError,
  } = usePortalLogout();
  const { unreadCount } = useUnreadNotifications();
  const languageSwitcherProps = {
    userType: profile?.user_type,
    profileId: profile?.id,
  } as const;
  const appliedLanguageDefaultRef = useRef(false);

  useEffect(() => {
    if (!profile?.id || profile.user_type !== 'subcontractor') return;
    if (appliedLanguageDefaultRef.current) return;
    appliedLanguageDefaultRef.current = true;
    applySubcontractorLanguageDefault(profile.id);
  }, [profile?.id, profile?.user_type]);

  const navItems = [
    { to: '/subcontractor/dashboard', icon: 'ri-building-2-line', label: t('nav.projects'), showProjectList: true },
    { to: '/subcontractor/instructions', icon: 'ri-file-list-3-line', label: t('nav.instructions') },
    { to: '/subcontractor/daily-log', icon: 'ri-calendar-line', label: t('nav.dailyLog') },
  ];

  const projectsNavActive =
    location.pathname.startsWith('/subcontractor/dashboard') ||
    location.pathname.startsWith('/subcontractor/project/');
  const projectIdMatch = location.pathname.match(/\/subcontractor\/project\/([^/]+)/);
  const currentProjectId = projectIdMatch?.[1];
  const onScheduleTab = new URLSearchParams(location.search).get('tab') === 'Schedule';

  const buildSubProjectUrl = useCallback(
    (projectId: string) => {
      if (onScheduleTab || location.pathname.includes('/subcontractor/project/')) {
        return `/subcontractor/project/${projectId}?tab=Schedule`;
      }
      return `/subcontractor/project/${projectId}`;
    },
    [onScheduleTab, location.pathname]
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex">

      {/* ── Desktop Sidebar ── */}
      <aside className="hidden lg:flex flex-col w-60 bg-[#1A1A1A] fixed top-0 left-0 bottom-0 z-40">
        {/* Logo block */}
        <div
          className="flex items-center justify-center px-5 cursor-pointer flex-shrink-0"
          style={{ background: '#FFFFFF', height: '64px', borderBottom: '1px solid #E5E2DC' }}
          onClick={() => navigate('/subcontractor/dashboard')}
        >
          <img
            src="https://qsenozfawbynwkmpxyjr.supabase.co/storage/v1/object/public/Logo/Logo_portal.png"
            alt="JPH Portal"
            style={{ maxHeight: '36px', maxWidth: '160px', objectFit: 'contain' }}
          />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <p className="text-white/25 text-[10px] font-semibold uppercase tracking-widest px-2 mb-2">{t('nav.menu')}</p>
          {navItems.map((item) => (
            <div key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#059669]/15 text-white border-l-2 border-[#059669]'
                      : 'text-white/50 hover:bg-white/5 hover:text-white border-l-2 border-transparent'
                  }`
                }
              >
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <span className="text-sm font-medium">{item.label}</span>
              </NavLink>
              {item.showProjectList && (
                <SidebarProjectsNavSection
                  active={projectsNavActive}
                  profileId={profile?.id ?? user?.id}
                  userType={profile?.user_type}
                  profileLoading={authLoading}
                  currentProjectId={currentProjectId}
                  buildProjectUrl={buildSubProjectUrl}
                />
              )}
            </div>
          ))}
        </nav>

        <div className="border-t border-white/5 px-3 py-3">
          <div className="px-3 pb-2">
            <LanguageSwitcher compact {...languageSwitcherProps} />
          </div>
          <SidebarAccountMenu
            displayName={accountName}
            loading={authLoading}
            portalLabel={t('user.jphPortal')}
            avatarClassName="bg-[#059669]"
            onChangePassword={() => setChangePasswordOpen(true)}
            onSignOut={openLogoutConfirm}
          />
          <p className="text-center mt-2 pb-1" style={{ color: '#BBBBBB', fontSize: '11px' }}>
            {t('user.producedBy')}
          </p>
        </div>
      </aside>

      {/* ── Mobile overlay sidebar ── */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)}></div>
          <aside className="relative w-64 bg-[#1A1A1A] flex flex-col h-full">
            <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E2DC' }}>
              <div className="flex items-center justify-between px-5" style={{ height: '64px' }}>
                <img
                  src="https://qsenozfawbynwkmpxyjr.supabase.co/storage/v1/object/public/Logo/Logo_portal.png"
                  alt="JPH Portal"
                  style={{ maxHeight: '32px', maxWidth: '140px', objectFit: 'contain' }}
                />
                <button onClick={() => setSidebarOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer flex-shrink-0">
                  <i className="ri-close-line text-[#374151] text-lg"></i>
                </button>
              </div>
            </div>
            <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
              {navItems.map((item) => (
                <div key={item.to}>
                  <NavLink
                    to={item.to}
                    onClick={() => setSidebarOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all cursor-pointer ${
                        isActive ? 'bg-[#059669]/15 text-white' : 'text-white/50 hover:bg-white/5 hover:text-white'
                      }`
                    }
                  >
                    <i className={`${item.icon} text-lg`}></i>
                    <span className="text-sm font-medium">{item.label}</span>
                  </NavLink>
                  {item.showProjectList && (
                    <SidebarProjectsNavSection
                      active={projectsNavActive}
                      profileId={profile?.id ?? user?.id}
                      userType={profile?.user_type}
                      profileLoading={authLoading}
                      currentProjectId={currentProjectId}
                      buildProjectUrl={buildSubProjectUrl}
                      onProjectClick={() => setSidebarOpen(false)}
                    />
                  )}
                </div>
              ))}
            </nav>
            <div className="border-t border-white/5 px-3 py-3">
              <div className="px-3 pb-2">
                <LanguageSwitcher compact {...languageSwitcherProps} />
              </div>
              <SidebarAccountMenu
                displayName={accountName}
                loading={authLoading}
                portalLabel={t('user.jphPortal')}
                avatarClassName="bg-[#059669]"
                onChangePassword={() => setChangePasswordOpen(true)}
                onSignOut={openLogoutConfirm}
                onAfterAction={() => setSidebarOpen(false)}
              />
              <p className="text-center mt-2 pb-1" style={{ color: '#BBBBBB', fontSize: '11px' }}>
                {t('user.producedBy')}
              </p>
            </div>
          </aside>
        </div>
      )}

      {/* ── Main area ── */}
      <div className="flex-1 flex flex-col lg:ml-60 min-h-screen min-w-0 max-w-full overflow-x-hidden">

        {/* ── Top Header ── */}
        <header
          className="sticky top-0 z-30 flex flex-col"
          style={{
            background: '#FFFFFF',
            borderBottom: '1px solid #E5E2DC',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          }}
        >
          <div className="flex items-center px-4 lg:px-6 gap-3" style={{ height: '64px' }}>
            {/* Left */}
            <div className="flex items-center gap-0 flex-1 min-w-0">
              {showBack ? (
                <button
                  onClick={() => navigate(backTo || (-1 as never))}
                  className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer flex-shrink-0 mr-2"
                  style={{ color: '#374151' }}
                >
                  <i className="ri-arrow-left-line text-lg"></i>
                </button>
              ) : (
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="lg:hidden w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer flex-shrink-0 mr-2"
                  style={{ color: '#374151' }}
                >
                  <i className="ri-menu-line text-lg"></i>
                </button>
              )}

              <span
                className="hidden lg:inline-flex items-center flex-shrink-0"
                style={{
                  background: '#F0F0F0',
                  color: '#333333',
                  border: '1px solid #CCCCCC',
                  fontSize: '11px',
                  padding: '3px 12px',
                  borderRadius: '20px',
                  fontWeight: 600,
                  letterSpacing: '0.3px',
                }}
              >
                {t('role.subcontractor')}
              </span>

              {title && (
                <h1 className="text-[15px] font-bold text-[#1A1A1A] truncate lg:ml-4 ml-0">{title}</h1>
              )}
            </div>

            {/* Right: language + actions + notifications + user menu */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <LanguageSwitcher {...languageSwitcherProps} />
              {actions}
              <button
                onClick={() => navigate('/subcontractor/notifications')}
                className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer relative"
                style={{ color: '#374151' }}
              >
                <i className="ri-notification-3-line text-lg"></i>
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#E8621A] rounded-full"></span>
                )}
              </button>
            </div>
          </div>

          <div style={{ height: '3px', background: '#F05A00', width: '100%' }} />
        </header>

        {/* Page content */}
        <main className="flex-1 pb-20 lg:pb-8 min-w-0 overflow-x-hidden">
          <div className="max-w-lg mx-auto lg:max-w-none">{children}</div>
        </main>
      </div>

      <LogoutConfirmModal
        open={logoutConfirm}
        isSigningOut={isSigningOut}
        error={logoutError}
        onCancel={closeLogoutConfirm}
        onConfirm={performLogout}
      />

      <ChangePasswordModal
        open={changePasswordOpen}
        email={user?.email ?? ''}
        onClose={() => setChangePasswordOpen(false)}
        onSignInAgain={performLogout}
      />

      {/* ── Mobile bottom tab bar ── */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-white border-t border-gray-100 flex items-center z-40">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => window.scrollTo({ top: 0, behavior: 'instant' })}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 cursor-pointer transition-colors ${
                isActive ? 'text-[#059669]' : 'text-[#9CA3AF]'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <span className={`text-[10px] font-medium ${isActive ? 'text-[#059669]' : 'text-[#9CA3AF]'}`}>
                  {item.label}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}